<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="false"
    :draggable="false"
    :style="{ width: 'clamp(320px, 92vw, 560px)' }"
    class="!bg-[var(--p-surface-100)] dark:!bg-[var(--p-surface-900)] px-8"
    @hide="handleClose"
  >
    <div class="dark:text-[var(--p-text-color)]">
      <div class="flex flex-col gap-4 pb-4 sm:flex-row sm:items-start">
        <div class="w-fit shrink-0 rounded-md bg-[var(--p-primary-color)]/20 p-1.5">
          <CheckCircle2 :size="30" class="text-[var(--p-primary-color)]" />
        </div>

        <div class="flex min-w-0 flex-col">
          <label
            class="montserrat text-2xl font-semibold leading-tight text-[var(--p-primary-color)] break-words"
          >
            {{ $t('whatsapp.transfer_accepted.title') }}
          </label>

          <label
            class="mt-0 text-xs leading-snug text-[var(--p-surface-600)] dark:text-[var(--p-surface-100)]"
          >
            {{ $t('whatsapp.transfer_accepted.description') }}
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-6">
        <div
          class="rounded-lg border border-[var(--p-surface-200)] bg-[var(--p-surface-0)] p-4 dark:border-[var(--p-surface-700)] dark:bg-[var(--p-surface-800)]"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--p-primary-color)]/15"
            >
              <UserRound :size="18" class="text-[var(--p-primary-color)]" />
            </div>

            <div class="min-w-0 flex-1">
              <p
                class="m-0 text-xs font-medium text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
              >
                {{ $t('whatsapp.transfer_accepted.contact') }}
              </p>
              <p class="m-0 mt-1 truncate text-base font-semibold dark:text-[var(--p-text-color)]">
                {{ contactName }}
              </p>
              <p
                class="m-0 truncate text-sm text-[var(--p-surface-500)] dark:text-[var(--p-surface-400)]"
              >
                {{ contactPhone }}
              </p>
            </div>
          </div>
        </div>

        <Message severity="success" :closable="false" size="small">
          {{ $t('whatsapp.transfer_accepted.success_message') }}
        </Message>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full flex-row items-center justify-center gap-5">
        <AppButtonIcon
          :label="$t('whatsapp.transfer_accepted.understood')"
          severity="primary"
          size="small"
          class="montserrat w-full whitespace-nowrap transition-colors duration-200 hover:opacity-90"
          @click="handleClose"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Message from 'primevue/message'
import { UserRound, CheckCircle2 } from 'lucide-vue-next'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'

interface Props {
  visible: boolean
  chatId: string
  contactName: string
  contactPhone: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
})

let autoCloseTimer: number | undefined

watch(
  () => props.visible,
  (visible) => {
    if (!visible) {
      return
    }

    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer)
    }

    autoCloseTimer = window.setTimeout(() => {
      handleClose()
    }, 5000)
  },
  { immediate: true },
)

const handleClose = () => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = undefined
  }

  emit('update:visible', false)
}

onBeforeUnmount(() => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
  }
})
</script>
